import React from 'react';
import { Link } from 'react-router-dom';
import { useDialog } from '../contexts/DialogContext';
import { WaitlistDialog } from './WaitlistDialog';

const tiers = [
  {
    name: 'Free',
    price: 'Free',
    priceNote: 'Up to 100 registered products — forever',
    description: 'Register your first 100 products free. See scan rates, registration data, and customer engagement in days.',
    features: [
      'Up to 100 registered products',
      'QR code generation & labels',
      'Product registration flow',
      'Digital product manual',
      'Basic warranty tracking',
      'Scan analytics',
      'No credit card required',
      'BrandedMark branding',
    ],
    cta: 'Join waitlist',
    highlighted: false,
  },
  {
    name: 'Grow',
    price: '£299',
    priceNote: '/month — up to 5,000 registered products',
    description: 'Full post-purchase platform. Warranty, ownership transfer, spare parts, and customer engagement — from one QR scan.',
    features: [
      'Up to 5,000 registered products',
      'Warranty management & claims',
      'Ownership transfer (resale-ready)',
      'Spare parts & accessories store',
      'Post-purchase engagement tools',
      'Experience Designer (no-code)',
      'API access',
      '5 team seats',
      'Removable BrandedMark branding',
      '£0.10/unit/month above 5,000',
    ],
    cta: 'Join waitlist',
    highlighted: true,
  },
  {
    name: 'Scale',
    price: '£999',
    priceNote: '/month — up to 50,000 registered products',
    description: 'For manufacturers with large product ranges. DPP compliance, AI product support, advanced analytics, and returns orchestration.',
    features: [
      'Up to 50,000 registered products',
      'Everything in Grow, plus:',
      'DPP/ESPR compliance layer',
      'AI-powered product support',
      'Returns & claims orchestration',
      'Advanced analytics & reporting',
      'Custom domain',
      'Unlimited team seats',
      'No BrandedMark branding',
      '£0.05/unit/month above 50,000',
    ],
    cta: 'Join waitlist',
    highlighted: false,
  },
];

const buildVsBuy = [
  { dimension: 'Time to launch', brandedmark: '2 weeks', inhouse: '12–18 months' },
  { dimension: 'Annual cost', brandedmark: 'From £0 (Free) to £3.6K/yr (Grow)', inhouse: '£150K+ engineering + 2-3 FTE' },
  { dimension: 'Product registration', brandedmark: '30-second QR scan flow', inhouse: 'Custom form + database' },
  { dimension: 'Ownership transfer', brandedmark: 'Built-in, works at resale', inhouse: 'Not built (too complex)' },
  { dimension: 'Spare parts commerce', brandedmark: 'QR scan to genuine parts', inhouse: 'Separate e-commerce build' },
  { dimension: 'DPP compliance', brandedmark: 'Add-on, updated automatically', inhouse: 'Build and maintain yourself' },
  { dimension: 'Ongoing maintenance', brandedmark: 'Included', inhouse: '2–3 FTE engineers' },
];

const faqs = [
  {
    q: 'What counts as a registered product?',
    a: 'A registered product is a physical unit with an active digital identity on BrandedMark — connected to an owner, with warranty, manual, and spare parts accessible via QR scan. The Free tier includes up to 100 registered products, enough to pilot on one product line.',
  },
  {
    q: 'What happens when I exceed my tier limit?',
    a: 'Your customers are never disrupted. Additional registered products are billed at your tier\'s per-unit rate (£0.10/unit on Grow, £0.05/unit on Scale). We cap monthly overage at 2x your base — no surprise bills.',
  },
  {
    q: 'Can I start free and upgrade later?',
    a: 'Yes. Start with 100 products free — no credit card, no time limit. When you see registration rates and customer engagement data, upgrade to Grow. All your data carries over.',
  },
  {
    q: 'How does this compare to building in-house?',
    a: 'BrandedMark Grow at £3.6K/year replaces £150K+ in custom development and 2-3 FTE engineers. You get product registration, warranty management, ownership transfer, spare parts commerce, and DPP compliance — ready in 2 weeks, not 18 months.',
  },
  {
    q: 'Do we own our data?',
    a: 'Absolutely. Your product data, customer registrations, and warranty records belong to you. We never sell your data, never train AI on your data, and never share it. Export anytime in JSON, CSV, or GS1 XML.',
  },
  {
    q: 'What about DPP / Digital Product Passport compliance?',
    a: 'DPP compliance is included on the Scale tier and available as an add-on for Grow. It covers GS1 Digital Link format, ESPR data structure, battery passport fields, and 10-year data retention — everything the regulation requires.',
  },
  {
    q: 'Is there an annual discount?',
    a: 'Yes. Pay annually and get 2 months free: Grow at £2,990/year or Scale at £9,990/year.',
  },
];

export const PricingPage: React.FC = () => {
  const { isDialogOpen, openDialog, closeDialog } = useDialog();

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {/* Header */}
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl" style={{ color: '#241E61' }}>
              Start free. Scale as you grow.
            </h1>
            <p className="mt-6 text-lg text-gray-600">
              Register your first 100 products free — no credit card, no time limit.
              When you're ready to scale, pay per registered product. Your cost grows with your customer relationships, not your headcount.
            </p>
          </div>

          {/* Pricing cards */}
          <div className="mx-auto max-w-5xl grid grid-cols-1 gap-8 lg:grid-cols-3">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className={`rounded-2xl p-8 flex flex-col ${
                  tier.highlighted
                    ? 'ring-2 ring-[#00B37E] bg-white shadow-xl relative'
                    : 'ring-1 ring-gray-200 bg-white'
                }`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-sm font-semibold text-white" style={{ background: '#00B37E' }}>
                    Most popular
                  </div>
                )}
                <h3 className="text-lg font-semibold" style={{ color: '#241E61' }}>{tier.name}</h3>
                <div className="mt-4 flex items-baseline gap-x-2">
                  <span className="text-4xl font-bold tracking-tight" style={{ color: '#241E61' }}>{tier.price}</span>
                </div>
                <p className="mt-1 text-sm text-gray-500">{tier.priceNote}</p>
                <p className="mt-4 text-sm text-gray-600">{tier.description}</p>

                <ul className="mt-8 space-y-3 flex-1">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-gray-600">
                      <svg className="w-5 h-5 text-[#00B37E] flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => openDialog('waitlist')}
                  className={`mt-8 w-full rounded-lg px-4 py-3 text-sm font-semibold transition-all duration-200 hover:opacity-90 border-none cursor-pointer ${
                    tier.highlighted
                      ? 'text-white shadow-lg'
                      : 'text-gray-900 ring-1 ring-gray-200 hover:ring-gray-300'
                  }`}
                  style={tier.highlighted ? { background: 'linear-gradient(135deg, #CFF710 0%, #00D084 100%)', color: '#1a1a1a' } : {}}
                >
                  {tier.cta}
                </button>
              </div>
            ))}
          </div>

          {/* Enterprise callout */}
          <div className="mx-auto max-w-5xl mt-8">
            <div className="rounded-2xl ring-1 ring-gray-200 bg-white p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-lg font-semibold" style={{ color: '#241E61' }}>Enterprise</h3>
                <p className="text-sm text-gray-600 mt-1">50,000+ products, multi-brand, white-label, SSO, 99.9% SLA, dedicated support, volume pricing, regulatory reporting.</p>
              </div>
              <button
                onClick={() => openDialog('waitlist')}
                className="rounded-lg px-6 py-3 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 hover:ring-gray-300 border-none cursor-pointer whitespace-nowrap transition-all"
              >
                Contact sales
              </button>
            </div>
          </div>

          {/* How your price grows */}
          <div className="mx-auto max-w-3xl mt-24">
            <h2 className="text-2xl font-semibold text-center mb-4" style={{ color: '#241E61' }}>
              How it works
            </h2>
            <p className="text-center text-gray-500 mb-8">
              One QR code per product. One platform for the full lifecycle. Your price scales with the number of products you connect.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 text-center">
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="text-2xl font-bold" style={{ color: '#00B37E' }}>100</div>
                <div className="text-sm text-gray-600 mt-2">Products registered free — prove the value first</div>
              </div>
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="text-2xl font-bold" style={{ color: '#00B37E' }}>£0.05–0.10</div>
                <div className="text-sm text-gray-600 mt-2">Per product per month above your tier — pennies, not pounds</div>
              </div>
              <div className="p-6 bg-gray-50 rounded-xl">
                <div className="text-2xl font-bold" style={{ color: '#00B37E' }}>2x cap</div>
                <div className="text-sm text-gray-600 mt-2">Monthly overage capped at 2x base — no surprise bills</div>
              </div>
            </div>
          </div>

          {/* Build vs Buy */}
          <div className="mx-auto max-w-4xl mt-24">
            <h2 className="text-2xl font-semibold text-center mb-2" style={{ color: '#241E61' }}>
              Build vs. buy
            </h2>
            <p className="text-center text-gray-500 mb-8">
              BrandedMark at £3.6K/year replaces £150K+ of in-house build — and it's ready in 2 weeks, not 18 months.
            </p>
            <div className="overflow-hidden rounded-xl ring-1 ring-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left px-6 py-3 font-semibold text-gray-900"></th>
                    <th className="text-left px-6 py-3 font-semibold" style={{ color: '#00B37E' }}>BrandedMark</th>
                    <th className="text-left px-6 py-3 font-semibold text-gray-500">Build in-house</th>
                  </tr>
                </thead>
                <tbody>
                  {buildVsBuy.map((row, i) => (
                    <tr key={row.dimension} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-3 font-medium text-gray-900">{row.dimension}</td>
                      <td className="px-6 py-3 text-gray-700">{row.brandedmark}</td>
                      <td className="px-6 py-3 text-gray-500">{row.inhouse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ */}
          <div className="mx-auto max-w-3xl mt-24">
            <h2 className="text-2xl font-semibold text-center mb-10" style={{ color: '#241E61' }}>
              Frequently asked questions
            </h2>
            <dl className="space-y-8">
              {faqs.map((faq) => (
                <div key={faq.q}>
                  <dt className="text-base font-semibold text-gray-900">{faq.q}</dt>
                  <dd className="mt-2 text-sm text-gray-600 leading-relaxed">{faq.a}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Bottom CTA */}
          <div className="mx-auto max-w-2xl mt-24 text-center">
            <h2 className="text-2xl font-semibold" style={{ color: '#241E61' }}>Ready to connect your products?</h2>
            <p className="mt-4 text-gray-600">Start with 100 products free. See registration rates, customer engagement, and spare parts demand in days.</p>
            <div className="mt-8 flex items-center justify-center gap-x-4">
              <button
                onClick={() => openDialog('waitlist')}
                className="rounded-lg px-6 py-3 text-base font-bold shadow-lg transition-all duration-200 hover:scale-105 border-none cursor-pointer"
                style={{
                  background: 'linear-gradient(135deg, #CFF710 0%, #00D084 100%)',
                  color: '#1a1a1a'
                }}
              >
                Join the waitlist
              </button>
              <Link
                to="/see-a-demo"
                className="text-base font-semibold no-underline hover:opacity-80"
                style={{ color: '#241E61' }}
              >
                See a demo <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <WaitlistDialog
        isOpen={isDialogOpen('waitlist')}
        onClose={() => closeDialog()}
      />
    </>
  );
};
